import axios from "axios";
import { buildApiUrl } from "../config";

const authService = {
  getCurrentUser: () => {
    const userStr = localStorage.getItem("user");

    return userStr ? JSON.parse(userStr) : null;
  },

  getAuthHeader: () => {
    const user = authService.getCurrentUser();
    if (user && user.token) {
      return { Authorization: `Bearer ${user.token}` };
    }
    return {};
  },

  login: async (username, password, recaptchaToken) => {
    try {
      delete axios.defaults.headers.common["Authorization"];

      const payload = { username, password };
      if (recaptchaToken) {
        payload.recaptchaToken = recaptchaToken;
      }

      const response = await axios.post(buildApiUrl("token/create/"), payload);

      if (response.data.token) {
        const token = response.data.token;

        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const userData = {
          ...response.data,
          username,
        };

        localStorage.setItem("user", JSON.stringify(userData));

        return userData;
      }
      return response.data;
    } catch (error) {
      delete axios.defaults.headers.common["Authorization"];

      const backendData = error.response?.data || {};
      const backendMessage = backendData.message || backendData.Message || null;
      const requiresRecaptcha =
        backendData.requiresRecaptcha ?? backendData.RequiresRecaptcha ?? false;

      if (error.response) {
        if (error.response.status === 401) {
          const err = new Error(
            backendMessage || "Credenciais inválidas. Por favor, tente novamente.",
          );
          err.status = 401;
          err.requiresRecaptcha = requiresRecaptcha;
          throw err;
        } else if (error.response.status === 423) {
          const err = new Error(
            backendMessage ||
              "Conta bloqueada temporariamente. Tente novamente mais tarde.",
          );
          err.status = 423;
          err.requiresRecaptcha = requiresRecaptcha;
          throw err;
        } else if (error.response.status === 400) {
          const err = new Error(
            backendMessage || "Erro de validação. Por favor, tente novamente.",
          );
          err.status = 400;
          err.requiresRecaptcha = requiresRecaptcha;
          throw err;
        } else if (error.response.status === 500) {
          const err = new Error(
            "Erro interno do servidor. Tente novamente mais tarde.",
          );
          err.status = 500;
          throw err;
        }
      }

      const err = new Error(
        error.message ||
          "Erro de conexão. Verifique sua internet e tente novamente.",
      );
      throw err;
    }
  },

  logout: async () => {
    const user = authService.getCurrentUser();
    if (user && user.refreshToken) {
      try {
        await axios.post(
          buildApiUrl("token/revoke"),
          { refreshToken: user.refreshToken },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      } catch (e) {
        /* silenciar — logout deve funcionar mesmo offline */
      }
    }

    localStorage.removeItem("user");

    if (axios.defaults.headers.common["Authorization"]) {
      delete axios.defaults.headers.common["Authorization"];
    }

    sessionStorage.clear();
  },

  isAuthenticated: () => {
    const user = authService.getCurrentUser();
    return !!user && !!user.token;
  },

  refreshAccessToken: async () => {
    const user = authService.getCurrentUser();
    if (!user || !user.refreshToken) {
      throw new Error("Refresh token não disponível.");
    }

    const response = await axios.post(buildApiUrl("token/refresh"), {
      refreshToken: user.refreshToken,
    });

    const { token: newToken, refreshToken: newRefreshToken } = response.data;

    const updatedUser = {
      ...user,
      token: newToken,
      refreshToken: newRefreshToken,
    };
    localStorage.setItem("user", JSON.stringify(updatedUser));

    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

    return newToken;
  },

  atualizarPerfil: async () => {
    try {
      const user = authService.getCurrentUser();
      if (!user || !user.token) {
        console.error("Usuário não autenticado");
        return null;
      }

      localStorage.setItem("user", JSON.stringify(user));

      return user;
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      return null;
    }
  },

  register: async (formData, recaptchaToken) => {
    try {
      const limpar = (valor) => (valor ?? "").replace(/\D/g, "");

      const tipoUsuarioNormal = 0;

      const request = {
        username: formData.username,
        senha: formData.senha,
        ConfirmarSenha: formData.ConfirmarSenha,
        email: formData.email,
        nome: formData.nome,
        sobrenome: formData.sobrenome,
        cpf: limpar(formData.cpf),
        telefone: limpar(formData.telefone),
        data_nascimento: formData.data_nascimento,
        recaptchaToken: recaptchaToken,
        endereco: {
          rua: formData.rua,
          numero: formData.numero,
          municipio: formData.cidade,
          estado: formData.estado,
          cep: formData.tipoUsuario !== 1 ? limpar(formData.cep) : null,
        },
        responsavelTecnico: {
          areaFormacao: formData.area_formacao,
          tiposDeEstudos: formData.tipo_estudo,
        },
        interessado: {
          profissao: formData.profissao,
          empresa: formData.empresa,
          cargo: formData.cargo,
        },
        pessoaJuridica: {
          razaoSocial: formData.razaoSocial,
          nomeFantasia: formData.nomeFantasia,
          dataConstituicao: formData.dataConstituicao,
          representanteLegal: formData.representanteLegal,
        },
        tipoUsuario: formData.tipoUsuario ?? tipoUsuarioNormal,
      };

      if (formData.tipoUsuario === 1) {
        delete request.endereco;
        delete request.interessado;
        delete request.pessoaJuridica;
      } else if (formData.tipoUsuario === 4) {
        delete request.responsavelTecnico;
        delete request.interessado;
      } else {
        delete request.responsavelTecnico;
        delete request.pessoaJuridica;
      }

      const response = await axios.post(buildApiUrl("usuario/"), request);

      return response.data;
    } catch (error) {
      console.error("Erro ao registrar usuário:", error);
      if (error.response) {
        throw error.response.data;
      }
      throw new Error("Erro ao realizar registro");
    }
  },
};

export default authService;
